import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType, CodeBlock, BlockType, ValidationMessage, ValidationMessageType,
} from '@appblocks/codegeneration/types';
import LUISItem from '@appblocks/codegeneration/SettingsConfigurations/LUISItem';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';

export default class LUISGroup extends BaseFeature {
    constructor() {
        super();
        this.name = 'Setting Group';
        this.id = 'luis_group';
        this.singleInstance = false;
        const settings = [
            {
                id: 'GROUPID',
                name: 'Group ID',
                settingType: SettingType.HIDDEN,
                required: true,
                default: 'group',
            },
            {
                id: 'NAME',
                name: 'Group Name',
                settingType: SettingType.STRING,
                required: true,
                default: 'General',
            },
            {
                id: 'SETTINGS',
                name: 'Settings',
                settingType: SettingType.ARRAY,
                refId: 'luis_item',
                required: true,
                showChildren: true,
                default: new Array<BaseFeature>(0),
            },
        ];
        this.settings = this.settings.concat(settings);
    }

    initSettings(project?: any, parentFeature?: any): void {
        let defaultIdValue = 'group';
        if (parentFeature) {
            switch (parentFeature.id) {
                case 'luis':
                    defaultIdValue = 'luisgroup';
                    break;
                case 'lcd_menu':
                    defaultIdValue = 'lcdgroup';
                    break;
                case 'web_dashboard':
                    defaultIdValue = 'webgroup';
                    break;
                case 'ds_man':
                    defaultIdValue = 'dsgroup';
                    break;
                default:
                    defaultIdValue = 'group';
                    break;
            }
        }
        for (let i = 0; i < this.settings.length; i++) {
            if (this.settings[i].id === 'GROUPID') {
                this.settings[i].default = defaultIdValue;
            }
        }

        super.initSettings(project, parentFeature, 'GROUPID');
    }


    getDependencies() {
        const dependencies = [
            'luis',
        ];
        return dependencies;
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const alphanumeric = /^[a-zA-Z0-9]+$/;
        const ID = this.getSettingValue('GROUPID');
        if (!ID.match(alphanumeric)) {
            errors.push({
                featureId: this.getID(),
                settingId: 'GROUPID',
                messageType: ValidationMessageType.ERROR,
                message: 'Group ID must be alphanumeric',
            });
        }

        return errors;
    }

    getCodeBlocks() {
        let blocks: Array<CodeBlock> = [];

        const ID = this.getSettingValue('GROUPID');
        const NAME = this.getSettingValue('NAME');
        const project = this.project;
        blocks.push({
            location: 'luis.xtxt',
            blockType: BlockType.CODE,
            template: `
I=${ID};D=${NAME};T=GROUP;`,
        });
        blocks.push({
            location: 'sdf.xtxt',
            blockType: BlockType.CODE,
            template: `
I=${ID};D=${NAME};T=GROUP;`,
        });

        blocks.push({
            location: 'menu.xtxt',
            blockType: BlockType.CODE,
            template: `
>>0\t0\tF\t\t${NAME}...\t^\n`,
        });

        let JSONConfigCode = '';

        const settings = this.getSettingValue('SETTINGS');
        const JSONItems = [];
        for (let i = 0; i < settings.length; i += 1) {
            blocks = blocks.concat(settings[i].getCodeBlocks());
            const luisItem = settings[i];
            const settingItem = project.getFeature(luisItem.getSettingValue('SETTINGID'));
            if (settingItem) {
                let options: any[] = [];
                const CONTROL = luisItem.getSettingValue('CONTROL');
                if (CONTROL === 'STATIC') {
                    options = luisItem.getSettingValue('OPTIONS').map((option: any) => {
                        return {
                            LABEL: option.getSettingValue('LABEL'),
                            VALUE: option.getSettingValue('VALUE'),
                        };
                    });
                }
                let dataType = 'STRING';
                let min;
                let max;
                switch (settingItem.getSettingValue('DATATYPE')) {
                    case 'F':
                    case 'B':
                    case 'W':
                        dataType = 'INT';
                        min = settingItem.getSettingValue('MIN');
                        max = settingItem.getSettingValue('MAX');
                        break;
                    case 'S':
                        dataType = 'STRING';
                        min = settingItem.getSettingValue('MIN') || 0;
                        max = settingItem.getSettingValue('MAX') || 240;
                        break;
                    case 'T':
                        dataType = 'DATETIME';
                        break;
                    case 'M':
                        dataType = 'TIME';
                        break;
                    default:
                        dataType = 'STRING';
                        break;
                }
                const JSONItem = {
                    variable: settingItem.getSettingValue('SETTINGID'),
                    name: luisItem.getSettingValue('NAME'),
                    control: luisItem.getSettingValue('CONTROL'),
                    readonly: luisItem.getSettingValue('READONLY'),
                    type: 'item',
                    groupId: ID,
                    dataType,
                    options,
                    min,
                    max,
                };
                JSONItems.push(JSONItem);
            }
        }
        // JSONConfigCode = JSON.stringify([
        //     {
        //         id: ID,
        //         name: NAME,
        //         type: 'group',
        //     },
        //     ...JSONItems,
        // ], null, 4);

        JSONConfigCode = [
            {
                id: ID,
                name: NAME,
                type: 'group',
            },
            ...JSONItems,
        ].map((item) => {
            return JSON.stringify(item, null, 4);
        }).join(',\n');

        blocks.push({
            location: 'LUIS_GROUP_ITEM',
            template: JSONConfigCode,
        });

        return blocks;
    }
}
